import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import { usePermission } from "../hooks/usePermission";
import { useUnsavedChangesGuard } from "../hooks/useUnsavedChangesGuard";
import ConfirmModal from "../components/ConfirmModal";
import TransferList from "../components/TransferList";
import { apiErrorMessage } from "../utils/timesheets";
import { categoryLabel } from "../utils/permissionLabels";

const inputClass =
  "w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500";
const labelClass = "block text-xs font-medium text-gray-600 mb-1";
const cardClass = "bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-5";

const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));
const fullName = (u) => [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || u.user_id;

/**
 * Pagina van één rol (/roles/:roleId): gegevens, rechten in twee kolommen, gebruikers met deze rol.
 * Wijzigingen worden pas bewaard met "Wijzigingen opslaan"; verlaten met niet-bewaarde wijzigingen vraagt bevestiging.
 */
export default function RoleDetail() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canManage = hasPermission("roles.manage");

  const [role, setRole] = useState(null);
  const [allPermissions, setAllPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [details, setDetails] = useState({ name: "", category: "", description: "", is_primary: false, parent_role_id: "" });
  const [permissionIds, setPermissionIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const apply = useCallback((data) => {
    setRole(data);
    setDetails({
      name: data.name,
      category: data.category || "",
      description: data.description || "",
      is_primary: !!data.is_primary,
      parent_role_id: data.parent_role_id ?? "",
    });
    setPermissionIds(data.permissions.map((p) => p.id));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [data, permissions, roleUsers, roles] = await Promise.all([
        apiFetch(`/roles/${roleId}`),
        apiFetch("/permissions"),
        apiFetch(`/roles/${roleId}/users`),
        apiFetch("/roles"),
      ]);
      apply(data);
      setAllRoles(Array.isArray(roles) ? roles : []);
      setAllPermissions(permissions);
      setUsers(roleUsers.users);
    } catch (err) {
      setLoadError(apiErrorMessage(err, "Fout bij ophalen rol"));
    } finally {
      setLoading(false);
    }
  }, [roleId, apply]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!message.text) return undefined;
    const timer = setTimeout(() => setMessage({ text: "", type: "" }), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  const originalIds = useMemo(() => (role ? role.permissions.map((p) => p.id) : []), [role]);
  const hierarchyChanged =
    !!role && (details.is_primary !== !!role.is_primary || String(details.parent_role_id ?? "") !== String(role.parent_role_id ?? ""));
  const detailsChanged =
    !!role &&
    (details.name !== role.name ||
      details.category !== (role.category || "") ||
      details.description !== (role.description || "") ||
      hierarchyChanged);
  const permissionsChanged = !!role && !sameSet(permissionIds, originalIds);
  const dirty = detailsChanged || permissionsChanged;
  const { prompt } = useUnsavedChangesGuard(dirty);

  const discard = () => apply(role);

  const save = async () => {
    if (!details.name.trim()) {
      setMessage({ text: "Een rol heeft een naam nodig.", type: "error" });
      return;
    }
    setSaving(true);
    setMessage({ text: "", type: "" });
    try {
      if (detailsChanged) {
        await apiFetch(`/roles/${roleId}`, {
          method: "PATCH",
          body: JSON.stringify({
            name: details.name.trim(),
            category: details.category.trim(),
            description: details.description.trim(),
            ...(hierarchyChanged
              ? { is_primary: details.is_primary, parent_role_id: details.is_primary || details.parent_role_id === "" ? null : Number(details.parent_role_id) }
              : {}),
          }),
        });
      }
      if (permissionsChanged) {
        await apiFetch(`/roles/${roleId}/permissions`, { method: "PUT", body: JSON.stringify(permissionIds) });
      }
      apply(await apiFetch(`/roles/${roleId}`));
      setMessage({ text: "Wijzigingen opgeslagen", type: "success" });
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, "Fout bij opslaan"), type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setConfirmDelete(false);
    try {
      await apiFetch(`/roles/${roleId}`, { method: "DELETE" });
      navigate("/roles");
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, "Fout bij verwijderen"), type: "error" });
    }
  };

  if (loading && !role) {
    return (
      <div className="p-6" aria-busy="true">
        <div className="mb-5 h-24 animate-pulse rounded-lg bg-gray-100" />
        <div className="h-64 animate-pulse rounded-lg bg-gray-100" />
      </div>
    );
  }

  if (loadError || !role) {
    return (
      <div className="p-6">
        <div className="rounded border border-red-200 bg-red-50 p-4 text-red-700">{loadError || "Rol niet gevonden"}</div>
        <Link to="/roles" className="mt-4 inline-block text-sm text-blue-600 hover:underline">← Terug naar rollen</Link>
      </div>
    );
  }

  const isAdminRole = role.name === "Administrator";

  return (
    <div className="p-6">
      {prompt}
      {confirmDelete && (
        <ConfirmModal
          title="Rol verwijderen"
          message={`De rol ${role.name} wordt definitief verwijderd.`}
          confirmLabel="Verwijderen"
          danger
          onConfirm={remove}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      <Link to="/roles" className="mb-3 inline-block text-sm text-blue-600 hover:underline">← Rollen</Link>

      <div className={`${cardClass} flex flex-wrap items-center gap-4`}>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl font-bold text-gray-900">{role.name}</h1>
          <div className="text-sm text-gray-500">
            {role.is_primary && (
              <span className="mr-2 rounded bg-blue-600 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">basisrol</span>
            )}
            {role.parent_name && <span className="mr-2">Onder {role.parent_name} ·</span>}
            {role.category || "Zonder categorie"} · {role.permissions.length} rechten · {users.length} gebruiker{users.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {message.text && (
        <div role="status" className={`mb-4 rounded border px-4 py-3 text-sm ${message.type === "success" ? "border-green-200 bg-green-50 text-green-800" : "border-red-200 bg-red-50 text-red-700"}`}>
          {message.text}
        </div>
      )}

      <section className={cardClass} aria-labelledby="gegevens">
        <h2 id="gegevens" className="mb-4 text-base font-semibold text-gray-800">Gegevens</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="role-name" className={labelClass}>Naam</label>
            <input id="role-name" className={inputClass} value={details.name} disabled={!canManage || isAdminRole}
              onChange={(e) => setDetails({ ...details, name: e.target.value })} />
            {isAdminRole && <p className="mt-1 text-xs text-gray-400">De rol Administrator kan niet hernoemd worden.</p>}
          </div>
          <div>
            <label htmlFor="role-category" className={labelClass}>Categorie</label>
            <input id="role-category" className={inputClass} value={details.category} disabled={!canManage}
              placeholder="bv. Voorraad, Verkoop, Timesheets" onChange={(e) => setDetails({ ...details, category: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="role-description" className={labelClass}>Beschrijving</label>
            <textarea id="role-description" rows={2} className={inputClass} value={details.description} disabled={!canManage}
              onChange={(e) => setDetails({ ...details, description: e.target.value })} />
          </div>
          {!isAdminRole && (
            <div className="grid gap-4 border-t border-gray-100 pt-4 md:col-span-2 md:grid-cols-2">
              <label htmlFor="role-primary" className="flex items-start gap-2 text-sm">
                <input id="role-primary" type="checkbox" className="mt-1" checked={details.is_primary}
                  disabled={!canManage || (role.children || []).length > 0}
                  onChange={(e) => setDetails({ ...details, is_primary: e.target.checked, parent_role_id: "" })} />
                <span>
                  <span className="font-medium text-gray-800">Primaire rol (basisrol)</span>
                  <span className="block text-xs text-gray-500">
                    Basis voor een menusectie: de absolute minimumset waarop andere rollen bouwen.
                    {(role.children || []).length > 0 && " Er hangen nog rollen onder, dus dit kan niet uitgezet worden."}
                  </span>
                </span>
              </label>
              {!details.is_primary && (
                <div>
                  <label htmlFor="role-parent" className={labelClass}>Vereist primaire rol</label>
                  <select id="role-parent" className={inputClass} value={details.parent_role_id} disabled={!canManage}
                    onChange={(e) => setDetails({ ...details, parent_role_id: e.target.value })}>
                    <option value="">Geen (zelfstandige rol)</option>
                    {allRoles.filter((r) => r.is_primary && r.id !== role.id).map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-400">Wie deze rol krijgt, krijgt de primaire rol er automatisch bij.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section className={cardClass} aria-labelledby="rechten">
        <h2 id="rechten" className="mb-1 text-base font-semibold text-gray-800">Rechten</h2>
        <p className="mb-4 text-sm text-gray-500">
          {canManage
            ? "Sleep rechten tussen de lijsten, of gebruik de knoppen per categorie om er meteen een heel blok toe te kennen of in te trekken."
            : "De rechten die deze rol geeft."}
          {isAdminRole && " De rol Administrator moet de rechten voor gebruikers- en rolbeheer behouden."}
        </p>
        <TransferList
          items={allPermissions}
          value={permissionIds}
          initial={originalIds}
          onChange={setPermissionIds}
          disabled={!canManage}
          noun="Recht"
          plural="rechten"
          metaOf={(p) => p.code}
          categoryLabel={categoryLabel}
          bulk
        />
      </section>

      {(role.children || []).length > 0 && (
        <section className={cardClass} aria-labelledby="onderliggend">
          <h2 id="onderliggend" className="mb-3 text-base font-semibold text-gray-800">
            Rollen onder deze basisrol <span className="font-normal text-gray-400">({role.children.length})</span>
          </h2>
          <ul className="divide-y divide-gray-100">
            {role.children.map((child) => (
              <li key={child.id} className="py-2 text-sm">
                <Link to={`/roles/${child.id}`} className="text-blue-600 hover:underline">{child.name}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className={cardClass} aria-labelledby="gebruikers">
        <h2 id="gebruikers" className="mb-3 text-base font-semibold text-gray-800">
          Gebruikers met deze rol <span className="font-normal text-gray-400">({users.length})</span>
        </h2>
        {users.length === 0 ? (
          <div className="text-sm text-gray-400">Niemand heeft deze rol.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {users.map((u) => (
              <li key={u.user_id} className="flex items-center justify-between py-2 text-sm">
                <Link to={`/users/${u.user_id}`} className="text-blue-600 hover:underline">{fullName(u)}</Link>
                <span className="text-xs text-gray-400">{u.is_active === false ? "Gedeactiveerd" : u.email}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {canManage && !isAdminRole && (
        <section className="mb-5 rounded-lg border border-red-200 bg-white p-5" aria-labelledby="gevaar">
          <h2 id="gevaar" className="mb-3 text-base font-semibold text-red-700">Gevaarlijke zone</h2>
          <button onClick={() => setConfirmDelete(true)} className="rounded bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700">
            Rol verwijderen
          </button>
          {users.length > 0 && <p className="mt-2 text-xs text-gray-500">Een rol met gebruikers kan niet verwijderd worden.</p>}
        </section>
      )}

      {dirty && (
        <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-white px-5 py-3 shadow-lg" role="region" aria-label="Niet-opgeslagen wijzigingen">
          <span className="text-sm text-gray-700">Je hebt niet-opgeslagen wijzigingen.</span>
          <div className="flex gap-2">
            <button onClick={discard} disabled={saving} className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">Annuleren</button>
            <button onClick={save} disabled={saving} className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Bezig…" : "Wijzigingen opslaan"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
